import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { ColorConstants } from "src/assets/custom/colors";
import { MinusCircleIcon } from "src/assets/images/svg/IconCollection";
import { RecipientQnAPlaceholder } from "src/Core/Common/Constants";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";
import { IRecipientModel } from "src/Core/ViewModels/Message/RecipientModel";
import { useDispatch } from "react-redux";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { ISecurityQuestionAnswer } from "src/Core/Domain/Compose/IComposeModel";
import { getRecipientDefaultQuestionAndAnswer, getRecipientQuestionAnswer } from "src/actionCreators/securityQuestionCreators";
import { debounce } from "throttle-debounce";

interface RecipientQuestionAnswerRowProps {
    data: IRecipientModel;
    questions: IMessageSecurityQuestion[];
    isLastRow: boolean;
    totalRows: number;
    onAddRow: () => void;
    onRemoveRow: (recipient: IRecipientModel) => void;
    onEditRow: (recipient: IRecipientModel) => void;
    onSaveRow: (recipient: IRecipientModel, data: IRecipientModel) => void;
    validateDuplicateRecipient: (email: string, index?: number) => boolean;
}

const RecipientQuestionAnswerRow: React.FC<RecipientQuestionAnswerRowProps> = props => {
    const [dropdownTitle, setDropdownTitle] = useState(getUsersDefaultQuestion().questionTitle);
    const [isQuestionAnswerLoading, setQuestionAnswerLoading] = useState<boolean>(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [isOnLoadEmailsQuestionFetched, setIsOnLoadEmailsQuestionFetched] = useState<boolean>(false);

    const dispatch = useDispatch();
    useEffect(() => {
        props.onSaveRow(props.data, {
            ...props.data,
            question: props.data.question || getUsersDefaultQuestion()?.question,
        });
    }, []);

    useEffect(() => 
    {
        setEmailAddress(props.data.emailAddress);
        if(!isOnLoadEmailsQuestionFetched)
        {
            setIsOnLoadEmailsQuestionFetched(true);
            onEmailAddressChange(props.data.emailAddress);
        }
    },[props.data.emailAddress]);

    useEffect(() => {
        setDropdownTitle(props.questions.find(x => x.messageSecurityQuestionId === props.data.questionId)?.questionTitle || getUsersDefaultQuestion().questionTitle);
    }, [props.data.questionId, props.data.questionId]);
    
  function getUsersDefaultQuestion(){
        return props.questions.find(x => x.isDefault) || props.questions[0];
    }

    const getUserQuestion = () => {
        return props.data.questionId ? props.data.question : getUsersDefaultQuestion()?.question;
    };
    const handleOnQuestionChange = (e: any) => {
        const questionId = +e.target.value;
        const question: string = props.questions.find(x => x.messageSecurityQuestionId === questionId)?.question ?? "";
        const questionTitle: string = props.questions.find(x => x.messageSecurityQuestionId === questionId)?.questionTitle ?? "";
      
        setDropdownTitle(questionTitle);

        setQuestionAnswerLoading(true);
      dispatch(
        getRecipientQuestionAnswer(
            props.data.emailAddress,
            questionId,
            (result: ISecurityQuestionAnswer) => {
                setQuestionAnswerLoading(false);
                const answer = !(result.answer)?"":result.answer;
                const isModified = true;
                props.onSaveRow(props.data, { ...props.data, question, answer ,questionId, isModified });
            }
        )
    );
    };

    const onEmailAddressChange = (value:string)=>
    {
        setQuestionAnswerLoading(true);
        const defaultQuestion : IMessageSecurityQuestion | undefined = props.questions.find(x=>x.isDefault);
        dispatch(
            getRecipientDefaultQuestionAndAnswer(
                value,
                (defaultQuestion?.messageSecurityQuestionId ?? 0),
                (result: ISecurityQuestionAnswer) => {
                    setQuestionAnswerLoading(false);
                    const answer = !(result.answer)?"":result.answer;
                    const question: string = props.questions.find(x => x.messageSecurityQuestionId === result.securityQuestionId)?.question ?? getUsersDefaultQuestion().question;
                    const questionTitle: string = props.questions.find(x => x.messageSecurityQuestionId === result.securityQuestionId)?.questionTitle ?? getUsersDefaultQuestion().questionTitle;
                    const questionId = props.questions.find(x => x.messageSecurityQuestionId === result.securityQuestionId)?.messageSecurityQuestionId ?? getUsersDefaultQuestion().messageSecurityQuestionId;
                    setDropdownTitle(questionTitle);

                    const isModified = false;
                    props.onSaveRow(props.data, { ...props.data, question, emailAddress: value, answer, questionId ,isModified });                    
                }
            )
        );
    };
    
    const debounceFn = useCallback(debounce(1000, onEmailAddressChange), []);
    
    const onChangeTextFieldValue = (event: any) => {
        const target = event.target;
        const value = event.target.value;
        const name = target.name;

        if(name === "emailAddress")
        {
            setEmailAddress(value);
            debounceFn(value); 
        }

        const data = {
            ...props.data,
            [name]: value,
        };
        props.onSaveRow(props.data, data);
    };

    const renderRemoveButton = () => {
        return (
            <a className="pointer btn-qna-action" onClick={() => props.onRemoveRow(props.data)} title="Remove">
                <MinusCircleIcon color={ColorConstants.grey} height={20} width={20} />
            </a>
        );
    };

    const renderActionButtons = () => {
        if (!props.isLastRow) return renderRemoveButton();
    };

    return (
        <LoadingOverlay style={{ height: "100%" }}>
        <Row className="align-items-center">
            <Col xs={3}>
                <Form.Control
                    type="text"
                    className="qna__email-input"
                    name="emailAddress"
                    disabled={props.data.isSaved}
                    value={emailAddress}
                    onChange={onChangeTextFieldValue}
                />
            </Col>
            <Col xs={2}>
                <Form.Select
                    className="qna__input default-select"
                    onChange={handleOnQuestionChange}
                    title={dropdownTitle}
                    disabled={props.data.isSaved}
                    value={props.data.questionId || getUsersDefaultQuestion()?.messageSecurityQuestionId}
                >
                    {props.questions.map((item, index) => (
                        <option key={index} value={item.messageSecurityQuestionId}>
                            {item.questionTitle}
                        </option>
                    ))}
                </Form.Select>
            </Col>
            <Col>
                <Form.Control
                    type="text"
                    className="qna__input"
                    name="question"
                    disabled={props.data.isSaved}
                    onChange={onChangeTextFieldValue}
                    value={getUserQuestion()}
                    title={getUserQuestion()}
                />
            </Col>
            <Col xs={2}>
                <Form.Control
                    type="text"
                    className="qna__input"
                    name="answer"
                    disabled={props.data.isSaved}
                    onChange={onChangeTextFieldValue}
                    value={props.data.answer}
                    title={props.data.answer}
                    placeholder={RecipientQnAPlaceholder.ANSWER}
                />
            </Col>
            <Col xs={1}>
                <span className="remove-button">{renderActionButtons()}</span>
            </Col>
        </Row>
        <Loader
            classNamePrefix="sm"
            text=""
            loading={isQuestionAnswerLoading}
          />
         </LoadingOverlay>
    );
};

export default RecipientQuestionAnswerRow;
